import Brand from 'data/Brand';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import BrandCard from './BrandCard';
import ParentBrandCard from './ParentBrandCard';
import FeatureFlag from 'data/FeatureFlag';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';

interface Props {
  brands: Brand[];
  featureFlags: FeatureFlag;
}

interface GroupedBrands {
  parentBrands: Map<number, { parent: Brand; children: Brand[] }>;
  regularBrands: Brand[];
}

const GridContainer = styled.div`
  column-count: 2;
  column-gap: 20px;
  margin-top: 24px;

  & > * {
    break-inside: avoid;
    page-break-inside: avoid;
    margin-bottom: 8px;
  }
`;

const groupBrandsByParentChild = (brands: Brand[]): GroupedBrands => {
  const parentBrandsMap = new Map<number, { parent: Brand; children: Brand[] }>();
  const regularBrands: Brand[] = [];
  const childBrandIds = new Set<number>();

  // First pass: identify parent brands and collect child brand IDs
  brands.forEach((brand) => {
    if (brand.isParentBrand) {
      parentBrandsMap.set(brand.id, { parent: brand, children: [] });
    }
    if (brand.parentId) {
      childBrandIds.add(brand.id);
    }
  });

  // Second pass: assign children to parents and identify regular brands
  brands.forEach((brand) => {
    if (brand.parentId && parentBrandsMap.has(brand.parentId)) {
      const parentGroup = parentBrandsMap.get(brand.parentId);
      if (parentGroup) {
        parentGroup.children.push(brand);
      }
    } else if (!brand.isParentBrand && !childBrandIds.has(brand.id)) {
      regularBrands.push(brand);
    }
  });

  // Sort parent brands: those with children first, then those without
  const sortedParentBrands = Array.from(parentBrandsMap.values()).sort((a, b) => {
    const aHasChildren = a.children.length > 0 ? 0 : 1;
    const bHasChildren = b.children.length > 0 ? 0 : 1;
    return aHasChildren - bHasChildren;
  });

  // Convert back to Map to maintain interface
  const parentBrands = new Map<number, { parent: Brand; children: Brand[] }>();
  sortedParentBrands.forEach(item => {
    parentBrands.set(item.parent.id, item);
  });

  return { parentBrands, regularBrands };
};

const BrandsListing: React.FC<Props> = ({ brands, featureFlags }: Props) => {
  const { readOnly } = useReadPermission(PermissionCode.BRANDS_VIEW_CREATE_DELETE);

  const groupedBrands = useMemo(() => groupBrandsByParentChild(brands), [brands]);

  const parentBrandEntries = Array.from(groupedBrands.parentBrands.entries());

  return (
    <GridContainer>
      {/* Render Parent Brands with their children */}
      {parentBrandEntries.map(([parentId, { parent, children }]) => (
        <ParentBrandCard
          key={parentId}
          parentBrand={parent}
          childBrands={children}
          featureFlags={featureFlags}
          readOnly={readOnly}
        />
      ))}

      {/* Render Regular Brands */}
      {groupedBrands.regularBrands.map((brand) => (
        <BrandCard
          key={brand.id}
          brand={brand}
          featureFlags={featureFlags}
          readOnly={readOnly}
        />
      ))}
    </GridContainer>
  );
};

export default React.memo(BrandsListing);
